<script setup lang="ts">
  import { ref, onMounted, computed, inject, type Ref } from 'vue'
  import { useToast } from '@/components/ui/toast'
  import { useFieldValidation } from '@/composables/useQuestionnaire'
  import { useTranslation } from '@/composables/useTranslation'
  import { type ClientQuestionnaireSubmission } from '@/types/generated'
  import { QuestionnaireSubmissionsService } from '@/types/generated'
  import MultiFileField from '@/components/admin/form/MultiFileField.vue'
  import type { Page } from '@/types'

  const questionnaire = inject<Ref<ClientQuestionnaireSubmission | null>>(
    'questionnaire',
    ref(null)
  )
  const rawSchema = inject<Ref<Page[]>>('rawSchema', ref([]))
  const { toast, dismiss: dismissToast } = useToast()
  const { t } = useTranslation({
    en: {
      success: 'Success',
      error: 'Error',
      fileDeletedSuccessfully: 'File deleted successfully',
      errorDeletingFile: 'Error deleting file'
    },
    de: {
      success: 'Erfolg',
      error: 'Fehler',
      fileDeletedSuccessfully: 'Datei erfolgreich gelöscht',
      errorDeletingFile: 'Fehler beim Löschen der Datei'
    }
  })

  const emit = defineEmits(['change', 'update:modelValue', 'filesChanged', 'validation-error'])

  const isUploading = ref(false)
  const files = ref<any[]>([])
  const multiFileRef = ref()

  const props = defineProps({
    field: {
      type: Object,
      default: () => ({})
    },
    modelValue: {
      type: Array as () => any[],
      default: () => []
    },
    isPreviewMode: {
      type: Boolean,
      default: false
    }
  })

  const maxFiles = computed(() => {
    // We don't want to limit the number of files for single file fields yet.
    // return props.field.type === 'filefield' ? 1 : undefined
    return undefined
  })

  const { validationError, handleInputChange } = useFieldValidation(props, emit)

  const findFieldPosition = (fieldUuid: string) => {
    if (!rawSchema.value || !Array.isArray(rawSchema.value)) {
      return null
    }

    for (let [pageIndex, page] of rawSchema.value.entries()) {
      if (page.items && Array.isArray(page.items)) {
        for (let [itemIndex, item] of page.items.entries()) {
          if (item.uuid === fieldUuid) {
            return { page: pageIndex, index: itemIndex }
          }
        }
      }
    }
    return null
  }

  const handleFilesChanged = async (newFiles: File[]) => {
    dismissToast()

    if (!questionnaire.value?.uuid) {
      toast({
        title: 'Fehler',
        description: 'Questionnaire nicht gefunden',
        variant: 'destructive'
      })
      return
    }

    const fieldPosition = findFieldPosition(props.field.uuid)
    if (!fieldPosition) {
      toast({
        title: 'Fehler',
        description: 'Feldposition konnte nicht ermittelt werden',
        variant: 'destructive'
      })
      return
    }

    isUploading.value = true

    try {
      const uploadedFiles = []
      const failureMessages = []

      console.log('newFiles', newFiles)

      for (const file of newFiles) {
        try {
          const response =
            await QuestionnaireSubmissionsService.questionnaireSubmissionsUploadFileCreate({
              uuid: questionnaire.value.uuid,
              formData: {
                file: file,
                page_index: fieldPosition.page,
                item_index: fieldPosition.index
              }
            })

          console.log('response', response)
          if (response) {
            uploadedFiles.push({
              ...response,
              name: file.name,
              size: file.size,
              type: file.type
            })
          }
          console.log('uploadedFiles', uploadedFiles)
        } catch (error) {
          if (
            typeof error === 'object' &&
            error !== null &&
            'status' in error &&
            error.status === 409
          ) {
            failureMessages.push(`Datei "${file.name}" ist bereits vorhanden`)
          } else {
            failureMessages.push(`Datei "${file.name}" konnte nicht hochgeladen werden`)
          }
        }
      }
      const updatedFiles = [...files.value, ...uploadedFiles]
      files.value = updatedFiles
      handleInputChange(updatedFiles)
      emit('filesChanged', updatedFiles)
      multiFileRef.value?.resetStage() // Avoid re-upload on next attempt

      // We prioritize bad news, because only one toast is shown at a time
      if (failureMessages.length > 0) {
        failureMessages.push(
          `${failureMessages.length} Datei${failureMessages.length > 1 ? 'en konnten' : ' konnte'} nicht hochgeladen werden`
        )
        const description = failureMessages.join('\n')
        toast({
          title: 'Warnung',
          description: description,
          variant: 'destructive'
        })
      } else {
        const isSingleFile = newFiles.length === 1
        const message = isSingleFile
          ? 'Datei erfolgreich hochgeladen'
          : `${uploadedFiles.length} Datei${uploadedFiles.length > 1 ? 'en' : ''} erfolgreich hochgeladen`

        toast({
          title: 'Erfolg',
          description: message,
          variant: 'default'
        })
      }
    } catch (error) {
      console.error('Upload error:', error)
      toast({
        title: 'Fehler',
        description: 'Fehler beim Hochladen der Dateien',
        variant: 'destructive'
      })
    } finally {
      isUploading.value = false
    }
  }

  const handleFileRemoved = async (file: any, index: number) => {
    const fileUuid = file.uuid
    if (fileUuid && questionnaire.value?.short_id) {
      try {
        const urlParams = new URLSearchParams(window.location.search)
        const accessToken = urlParams.get('access_token')

        const response = await fetch(
          `/api/v3/questionnaire_submissions/${questionnaire.value?.short_id || ''}/delete-file/`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken':
                document.cookie
                  .split('; ')
                  .find((row) => row.startsWith('csrftoken='))
                  ?.split('=')[1] || '',
              ...(accessToken && { 'X-Access-Token': accessToken })
            },
            body: JSON.stringify({
              uuid: fileUuid
            })
          }
        )

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        toast({
          title: t('success'),
          description: t('fileDeletedSuccessfully'),
          variant: 'default'
        })
      } catch (error) {
        console.error('Error deleting file:', error)
        toast({
          title: t('error'),
          description: t('errorDeletingFile'),
          variant: 'destructive'
        })
        return
      }
    }

    const updatedFiles = files.value.filter((f: any) => f.uuid !== fileUuid || f.name !== file.name)
    files.value = updatedFiles
    handleInputChange(updatedFiles)
  }

  onMounted(() => {
    const fieldPosition = findFieldPosition(props.field.uuid)
    if (fieldPosition && rawSchema.value) {
      try {
        const schemaField = rawSchema.value[fieldPosition.page]?.items[fieldPosition.index]
        if (schemaField && schemaField.value && Array.isArray(schemaField.value)) {
          const schemaFiles = schemaField.value.map((file: any) => ({
            uuid: file.uuid,
            url: file.url,
            filename: file.filename,
            title: file.title,
            name: file.filename || file.title,
            size: file.size || 0,
            type: file.type || 'application/octet-stream',
            hash: file.hash
          }))
          files.value = schemaFiles
          handleInputChange(schemaFiles)
        }
      } catch (error) {
        console.error('Error updating files from schema:', error)
      }
    }
  })
</script>

<template>
  <div class="w-full pb-4">
    <MultiFileField
      ref="multiFileRef"
      :key="files.length"
      :name="field.uuid"
      :allowed-file-extensions="['pdf', 'png', 'jpg', 'jpeg']"
      :disabled="isPreviewMode"
      :model-value="files"
      :max-files="maxFiles"
      @update:model-value="handleFilesChanged"
      @file-removed="handleFileRemoved"
    />

    <div
      v-if="validationError"
      class="mt-2 text-sm text-red-600"
    >
      {{ validationError }}
    </div>

    <div
      v-if="isUploading"
      class="mt-4 flex items-center justify-center"
    >
      <div class="h-6 w-6 animate-spin rounded-full border-b-2 border-gray-900"></div>
      <span class="ml-2 text-sm text-gray-600">Dateien werden hochgeladen...</span>
    </div>
  </div>
</template>

<style scoped></style>
